import { useEffect, useLayoutEffect } from 'react';
import type { PropsWithChildren } from 'react';
import { useWizard } from '@zillow/zrs-rental-applications';
import {
  useId,
  useIdVerificationCallbacks,
  useLoggedIn,
  useReauthenticating,
  useSetLoggedIn,
  useSetReauthenticating,
  useSetSkipTermsStep,
  useSkipLandingStep,
  useSkipTermsStep,
  useTermSet,
} from '../contexts/IdVerificationContext';
import { ExitReason, IdvStep, LogLevel } from '../../../models';
import {
  useIdVerificationWebApiHandler_getTermSet,
  useIdVerificationWebApiHandler_isUserIdentified,
} from '@zg-rentals/ts-id-verification-client-react-query';
import { getZgAuthZoneFromTermSet } from '../utils/zgAuthZoneUtils';
import { termSetToTermSetId } from '../utils/termSetUtils';
import {
  FROZEN_ACCOUNT_WARNING_SUBSTEP,
  GENERIC_ERROR_SUBSTEP,
  USER_LOCKED_OUT_SUBSTEP,
  USER_NOT_FOUND_LOCKED_OUT_SUBSTEP,
} from '../constants/steps';
import IdvSpinner from '../IdvSpinner';
import { allTermsAccepted } from '../Content/TermsStep/TermStep';
import { IdentificationStatusType } from '@zg-rentals/ts-id-verification-client';

export default function IsIdentifiedAndTermsWrapper({ children }: PropsWithChildren) {
  const { onRequestStep } = useWizard();
  const loggedIn = useLoggedIn();
  const setLoggedIn = useSetLoggedIn();
  const { onExit, onNotAuthenticated, log } = useIdVerificationCallbacks();
  const skipLandingStep = useSkipLandingStep();
  const setSkipTermsStep = useSetSkipTermsStep();
  const skipTermsStep = useSkipTermsStep();
  const reauthenticating = useReauthenticating();
  const setReauthenticating = useSetReauthenticating();
  const requestingClient = useId();
  const termSet = useTermSet();
  const termSetId = termSetToTermSetId(termSet);

  const {
    isLoading,
    data: isUserIdentifiedResponse,
    error,
    isSuccess,
  } = useIdVerificationWebApiHandler_isUserIdentified({
    requestingClient,
    zgAuthZone: getZgAuthZoneFromTermSet(termSet),
  });

  const identificationStatusType = reauthenticating
    ? IdentificationStatusType.REAUTHENTICATING
    : isUserIdentifiedResponse?.identificationStatusType;

  useEffect(() => {
    if (isSuccess) {
      if (!loggedIn) {
        setLoggedIn(true);
      }
      if (identificationStatusType === IdentificationStatusType.REAUTHENTICATING) {
        setReauthenticating(true);
      }
    }
  }, [isSuccess, identificationStatusType, loggedIn, setLoggedIn, setReauthenticating]);

  const {
    isLoading: isTermSetLoading,
    isSuccess: isTermSetSuccess,
    data: termSetData,
    isError: isTermSetError,
    error: termSetError,
  } = useIdVerificationWebApiHandler_getTermSet({ termSetId });
  const areAllTermsAccepted = allTermsAccepted(termSetData?.terms) ?? false;

  useEffect(() => {
    if (isTermSetError) {
      log(LogLevel.Error, {
        message: 'Error retrieving term set',
        error: termSetError,
      });
      onRequestStep(IdvStep.ErrorStep, GENERIC_ERROR_SUBSTEP);
    }
  }, [isTermSetError, log, onRequestStep, termSetError]);

  useEffect(() => {
    if (isTermSetSuccess && termSetData && skipTermsStep === null) {
      setSkipTermsStep(areAllTermsAccepted ?? false);
    }
  }, [areAllTermsAccepted, isTermSetSuccess, setSkipTermsStep, skipTermsStep, termSetData]);

  useLayoutEffect(() => {
    const handleError = async () => {
      if (!isLoading && error) {
        await onNotAuthenticated();
        await onExit({ reason: ExitReason.NotAuthenticated, success: false });
      }
    };

    const handleIdvResponse = async () => {
      if (!isLoading) {
        switch (identificationStatusType) {
          case IdentificationStatusType.CURRENTLY_IDENTIFIED:
            if (!areAllTermsAccepted) {
              onRequestStep(IdvStep.TermsStep);
            } else {
              await onExit({ reason: ExitReason.CurrentlyIdentified, success: true });
            }
            break;
          case IdentificationStatusType.FROZEN_CREDIT_FILE:
            onRequestStep(IdvStep.ErrorStep, FROZEN_ACCOUNT_WARNING_SUBSTEP);
            break;
          case IdentificationStatusType.LOCKED_OUT:
            onRequestStep(IdvStep.ErrorStep, USER_LOCKED_OUT_SUBSTEP);
            break;
          case IdentificationStatusType.NOT_IDENTIFIED:
          case IdentificationStatusType.PREVIOUSLY_IDENTIFIED:
            break;
          case IdentificationStatusType.REAUTHENTICATING:
            if (!areAllTermsAccepted) {
              onRequestStep(IdvStep.TermsStep);
            } else {
              onRequestStep(IdvStep.PersonalInfoStep);
            }
            break;
          case IdentificationStatusType.USER_NOT_FOUND_LOCKED_OUT:
            onRequestStep(IdvStep.ErrorStep, USER_NOT_FOUND_LOCKED_OUT_SUBSTEP);
            break;
          default:
            log(LogLevel.Warn, {
              message: `Unknown identificationStatusType: ${identificationStatusType}`,
            });
            if (!isLoading) {
              onRequestStep(IdvStep.ErrorStep, GENERIC_ERROR_SUBSTEP);
            }
            break;
        }
      }
    };
    handleError();
    handleIdvResponse();
  }, [
    areAllTermsAccepted,
    isLoading,
    identificationStatusType,
    termSetData,
    loggedIn,
    error,
    onRequestStep,
    onExit,
    onNotAuthenticated,
    log,
  ]);

  useLayoutEffect(() => {
    const notIdentified =
      identificationStatusType === IdentificationStatusType.NOT_IDENTIFIED ||
      identificationStatusType === IdentificationStatusType.PREVIOUSLY_IDENTIFIED;
    if (!isLoading && (skipLandingStep || reauthenticating) && notIdentified) {
      if (areAllTermsAccepted) {
        onRequestStep(IdvStep.PersonalInfoStep);
      } else {
        onRequestStep(IdvStep.TermsStep);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified for staleness
  }, [isLoading, isTermSetLoading, skipLandingStep, onRequestStep, reauthenticating]);

  if (
    loggedIn &&
    (identificationStatusType !== IdentificationStatusType.CURRENTLY_IDENTIFIED || !areAllTermsAccepted)
  ) {
    return children;
  }

  return <IdvSpinner />;
}
